/**
 * Orders Service Types
 *
 * Types and interfaces for the orders service endpoints
 * Handles order management, status tracking, and reporting
 */

/**
 * Tipo de pedido atendido pela fila.
 *
 * Estava sendo importado de dentro do app fila (@/apps/fila/src/types/config),
 * uma inversão de dependência: shared/ é consumido pelos apps, não o contrário.
 * O alias sequer resolvia fora daquele app. Agora a definição mora aqui e o
 * app reexporta.
 */
export type OrderType = 'lunchbox' | 'queue';

// Base order types
interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes?: string;
  product_image?: string;
  product_category?: string;
}

interface Order {
  id: string;
  establishment_id: string;
  student_id: string;
  interval_id?: string;
  order_number: string;
  status: OrderStatus;
  items: OrderItem[];
  total_amount: number;
  payment_method?: string;
  payment_status?: 'pending' | 'paid' | 'failed' | 'refunded';
  notes?: string;
  created_at: string;
  updated_at: string;
  delivered_at?: string;

  // Customer information (populated in responses)
  student_name?: string;
  student_class?: string;
  establishment_name?: string;
  interval_name?: string;

  // Order tracking information
  preparation_time?: number; // minutes
  estimated_delivery?: string;
  delivery_address?: string;
  special_instructions?: string;
}

// Order status enum with Brazilian Portuguese values
enum OrderStatus {
  PENDING = 'pendente',
  IN_PREPARATION = 'em_preparo',
  READY = 'pronto',
  DELIVERED = 'entregue',
  CANCELLED = 'cancelado',
}

// Request types for each endpoint
interface TodayOrdersParams {
  establishment_id: string;
  status?: OrderStatus;
  interval_id?: string;
  orderType?: OrderType | 'lunchbox';
  page?: number;
  limit?: number;
  sort_by?: 'created_at' | 'order_number' | 'student_name' | 'total_amount';
  sort_order?: 'asc' | 'desc';
}

interface OrderSummaryParams {
  establishment_id: string;
  date?: string; // YYYY-MM-DD format, defaults to today
}

interface OrderSummaryAllParams {
  establishment_id: string;
  date_from?: string; // YYYY-MM-DD format
  date_to?: string; // YYYY-MM-DD format
}

interface UpdateOrderStatusRequest {
  status: OrderStatus;
  notes?: string;
  delivery_time?: string;
  updated_by?: string;
}

interface OrderDetailParams {
  id: string;
  include_items?: boolean;
  include_history?: boolean;
}

interface CurrentIntervalsParams {
  establishment_id: string;
}

// Response types
interface TodayOrdersResponse {
  orders: Order[];
  total: number;
  pagination?: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
  };
  summary: {
    total_orders: number;
    // chaveado pelo status cru da API ('fila','preparando','pronto','entregue'),
    // que não coincide com os valores do enum OrderStatus
    by_status: Record<string, number>;
    total_revenue: number;
  };
}

interface OrderSummary {
  date: string;
  establishment_id: string;
  total_orders: number;
  total_revenue: number;
  average_order_value: number;
  // chaveado pelo status cru da API ('fila','preparando','pronto','entregue'),
  // que não coincide com os valores do enum OrderStatus
  by_status: Record<string, number>;
  by_payment_method: Record<string, number>;
  popular_items: Array<{
    product_id: string;
    product_name: string;
    quantity_sold: number;
    revenue: number;
  }>;
  hourly_distribution: Array<{
    hour: number;
    order_count: number;
    revenue: number;
  }>;
}

interface OrderSummaryResponse {
  summary: OrderSummary;
  recent_orders: Order[];
}

interface OrderSummaryAllResponse {
  summaries: OrderSummary[];
  total_period: {
    total_orders: number;
    total_revenue: number;
    average_daily_orders: number;
    average_daily_revenue: number;
    growth_rate: number;
  };
  trends: {
    daily_orders: Array<{ date: string; count: number }>;
    daily_revenue: Array<{ date: string; amount: number }>;
    status_distribution: Record<OrderStatus, number>;
  };
}

interface UpdateOrderStatusResponse {
  order: Order;
  message: string;
  success: boolean;
  status_history?: OrderStatusHistory[];
}

interface OrderDetailResponse {
  order: Order;
  status_history?: OrderStatusHistory[];
  related_orders?: Order[]; // Other orders from same student
}

interface IntervalResponse {
  intervals: Interval[];
  current_interval?: Interval;
  total: number;
}

// Resposta simples do endpoint /orders/intervals/current
interface CurrentIntervalResponse {
  interval_id: string;
  name: string;
  start_time?: string;
  end_time?: string;
  is_default?: boolean;
  resolved_at?: string;
  order_count?: number;
}

export interface Interval {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  is_active: boolean;
  establishment_id: string;
  order_count?: number;
}

// Order status tracking
interface OrderStatusHistory {
  id: string;
  order_id: string;
  old_status: OrderStatus;
  new_status: OrderStatus;
  changed_by: string;
  changed_at: string;
  notes?: string;
  estimated_time?: number; // minutes until next status
}

// Search and filter types
interface OrderSearchParams {
  establishment_id?: string;
  student_id?: string;
  interval_id?: string;
  status?: OrderStatus;
  payment_status?: 'pending' | 'paid' | 'failed' | 'refunded';
  date_from?: string;
  date_to?: string;
  order_number?: string;
  student_name?: string;
  min_amount?: number;
  max_amount?: number;
}

interface OrderListParams extends OrderSearchParams {
  page?: number;
  limit?: number;
  sort_by?: OrderSortField;
  sort_order?: 'asc' | 'desc';
  include_items?: boolean;
  include_summary?: boolean;
}

// Statistics and reporting types
interface OrderStats {
  total_orders: number;
  total_revenue: number;
  average_order_value: number;
  // chaveado pelo status cru da API ('fila','preparando','pronto','entregue'),
  // que não coincide com os valores do enum OrderStatus
  by_status: Record<string, number>;
  by_establishment: Record<string, number>;
  by_payment_method: Record<string, number>;
  by_date: Record<string, number>;
  peak_hours: Array<{
    hour: number;
    order_count: number;
    percentage: number;
  }>;
  top_products: Array<{
    product_id: string;
    product_name: string;
    quantity_sold: number;
    revenue: number;
    order_count: number;
  }>;
  completion_times: {
    average_preparation_time: number;
    average_delivery_time: number;
    // chaveado pelo status cru da API ('fila','preparando','pronto','entregue'),
    // que não coincide com os valores do enum OrderStatus
    by_status: Record<string, number>;
  };
}

interface OrderDashboardData {
  today_stats: OrderStats;
  week_stats: OrderStats;
  month_stats: OrderStats;
  recent_orders: Order[];
  pending_orders: Order[];
  alerts: Array<{
    type: 'warning' | 'error' | 'info';
    message: string;
    order_id?: string;
    timestamp: string;
  }>;
}

// Validation types
interface OrderValidation {
  establishment_id: {
    required: boolean;
    format: 'uuid';
  };
  student_id: {
    required: boolean;
    format: 'uuid';
  };
  items: {
    required: boolean;
    min_items: number;
    max_items: number;
  };
  total_amount: {
    required: boolean;
    min_value: number;
    max_value: number;
  };
  status: {
    required: boolean;
    allowed_transitions: Record<OrderStatus, OrderStatus[]>;
  };
  notes: {
    required: boolean;
    max_length: number;
  };
}

// Default validation rules
const ORDER_VALIDATION_RULES: OrderValidation = {
  establishment_id: {
    required: true,
    format: 'uuid',
  },
  student_id: {
    required: true,
    format: 'uuid',
  },
  items: {
    required: true,
    min_items: 1,
    max_items: 50,
  },
  total_amount: {
    required: true,
    min_value: 0.01,
    max_value: 1000.0,
  },
  status: {
    required: true,
    allowed_transitions: {
      [OrderStatus.PENDING]: [OrderStatus.IN_PREPARATION, OrderStatus.CANCELLED],
      [OrderStatus.IN_PREPARATION]: [OrderStatus.READY, OrderStatus.CANCELLED],
      [OrderStatus.READY]: [OrderStatus.DELIVERED, OrderStatus.CANCELLED],
      [OrderStatus.DELIVERED]: [], // Final status
      [OrderStatus.CANCELLED]: [], // Final status
    },
  },
  notes: {
    required: false,
    max_length: 500,
  },
};

// Form and UI types
interface OrderFormData {
  establishment_id: string;
  student_id: string;
  interval_id: string;
  items: OrderItem[];
  notes: string;
  payment_method: string;
  special_instructions: string;
}

interface OrderFormErrors {
  establishment_id?: string;
  student_id?: string;
  interval_id?: string;
  items?: string;
  notes?: string;
  total_amount?: string;
  payment_method?: string;
  general?: string;
}

// Calendar and timeline types
interface OrderCalendarEntry {
  date: string; // YYYY-MM-DD
  orders: Order[];
  total_orders: number;
  total_revenue: number;
  status_counts: Record<OrderStatus, number>;
}

interface OrderTimelineEntry {
  time: string; // HH:MM
  orders: Order[];
  order_count: number;
  revenue: number;
}

// Batch operations
interface BatchOrderOperation {
  operation: 'update_status' | 'cancel' | 'assign_delivery';
  order_ids: string[];
  data: {
    status?: OrderStatus;
    notes?: string;
    delivery_person?: string;
  };
}

interface BatchOrderResult {
  successful: number;
  failed: number;
  errors: Array<{
    order_id: string;
    error: string;
  }>;
  results: Order[];
}

// API Response wrapper
interface OrderApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  timestamp?: string;
}

// Common parameters
interface OrderParams {
  order_id: string;
}

interface EstablishmentOrderParams {
  establishment_id: string;
}

interface StudentOrderParams {
  student_id: string;
}

// Utility types
type OrderSortField = 'created_at' | 'updated_at' | 'order_number' | 'student_name' | 'total_amount' | 'status' | 'delivered_at';

type OrderSortOrder = 'asc' | 'desc';

type OrderStatusTransition = {
  from: OrderStatus;
  to: OrderStatus;
  allowed: boolean;
  requires_confirmation?: boolean;
};

// Report types
interface OrderReport {
  title: string;
  period: {
    start: string;
    end: string;
  };
  establishment_id?: string;
  filters: OrderSearchParams;
  data: {
    summary: OrderStats;
    orders: Order[];
    charts: Array<{
      type: 'line' | 'bar' | 'pie';
      title: string;
      data: unknown;
    }>;
  };
  generated_at: string;
  generated_by: string;
}

// Kitchen management types
interface KitchenOrder {
  id: string;
  order_number: string;
  items: OrderItem[];
  status: OrderStatus;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  estimated_time: number; // minutes
  notes: string;
  student_name: string;
  created_at: string;
  started_at?: string;
  special_requirements: string[];
}

interface KitchenDashboard {
  pending_orders: KitchenOrder[];
  in_preparation: KitchenOrder[];
  ready_orders: KitchenOrder[];
  average_preparation_time: number;
  orders_per_hour: number;
  efficiency_score: number;
}

// Export all types for easy importing
export type {
  Order,
  OrderItem,
  TodayOrdersParams,
  TodayOrdersResponse,
  OrderSummaryParams,
  OrderSummaryResponse,
  OrderSummaryAllParams,
  OrderSummaryAllResponse,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  OrderDetailParams,
  OrderDetailResponse,
  OrderSummary,
  OrderStatusHistory,
  OrderSearchParams,
  OrderListParams,
  OrderStats,
  OrderDashboardData,
  OrderValidation,
  OrderFormData,
  OrderFormErrors,
  OrderCalendarEntry,
  OrderTimelineEntry,
  BatchOrderOperation,
  BatchOrderResult,
  OrderApiResponse,
  OrderParams,
  EstablishmentOrderParams,
  StudentOrderParams,
  OrderSortField,
  OrderSortOrder,
  OrderStatusTransition,
  OrderReport,
  KitchenOrder,
  KitchenDashboard,
  IntervalResponse,
  CurrentIntervalsParams,
  CurrentIntervalResponse,
};

// Export enums
export { OrderStatus };

// ==================== WEBSOCKET TYPES ====================

// WebSocket connection status
export type WebSocketConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

// WebSocket message types
export interface WebSocketOrderMessage {
  type: 'orders_update' | 'initial_orders' | 'order_status_change' | 'heartbeat' | 'error' | 'UPDATE' | 'connected';
  timestamp?: string;
  establishment_id?: string;

  // For full orders updates
  orders?: Order[];

  // For single order updates
  order?: Order;

  // Real-time update events (new structure)
  action?: 'CREATE' | 'INSERT' | 'UPDATE' | 'DELETE';
  order_id?: string;
  order_number?: number;
  status?: string;
  student_name?: string;
  class_name?: string;
  interval_id?: string;
  ts?: string; // timestamp from server

  // Error information
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };

  // Additional metadata
  metadata?: {
    total_orders?: number;
    last_updated?: string;
    update_type?: 'full' | 'incremental';
  };
}

// A configuração do serviço de WebSocket vive em websocketService.ts, junto da
// implementação. Havia uma segunda declaração aqui, com o contrato antigo
// (payload 'raw' | 'processed', sem intervalId), e o index.ts reexportava as
// duas — de onde vinha o "Duplicate identifier". Ninguém importava esta.

// WebSocket connection state for React hooks
export interface WebSocketState {
  status: WebSocketConnectionStatus;
  orders: Order[];
  error: Error | null;
  isConnected: boolean;
  isConnecting: boolean;
  lastUpdated: string | null;
}

// Raw API order format (from WebSocket 'raw' payload)
export interface RawAPIOrder {
  id: string;
  order_number: number;
  student_id: string;
  user_id: string;
  transaction_id: string;
  status: string; // 'fila', 'em_preparo', 'pronto', 'entregue', 'cancelado'
  queue_date: string;
  created_at: string;
  establishment_id: string;
  student_name: string;
  class_name: string; // Padronizado: usar class_name em vez de student_class
  channel: string;
  products: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    item_price: number;
  }>;
  interval_id?: string;
  interval_name?: string;
  updated_at?: string;
  delivered_at?: string;
  notes?: string;
}

// Function to transform raw API order to Order type
export function transformRawOrderToOrder(rawOrder: RawAPIOrder): Order {
  return {
    id: rawOrder.id,
    establishment_id: rawOrder.establishment_id,
    student_id: rawOrder.student_id,
    interval_id: rawOrder.interval_id,
    order_number: rawOrder.order_number.toString(),
    status: mapStatusStringToOrderStatus(rawOrder.status),
    items: rawOrder.products.map((product) => ({
      id: product.product_id,
      product_id: product.product_id,
      product_name: product.product_name,
      quantity: product.quantity,
      unit_price: product.item_price,
      total_price: product.item_price * product.quantity,
    })),
    total_amount: rawOrder.products.reduce((sum, product) => sum + product.item_price * product.quantity, 0),
    created_at: rawOrder.created_at,
    updated_at: rawOrder.updated_at || rawOrder.created_at,
    delivered_at: rawOrder.delivered_at,
    student_name: rawOrder.student_name,
    student_class: rawOrder.class_name, // Padronizado: usar class_name
    establishment_name: undefined, // Will be populated if needed
    interval_name: rawOrder.interval_name,
  };
}

// Helper function to map status strings to OrderStatus enum
function mapStatusStringToOrderStatus(status: string): OrderStatus {
  switch (status) {
    case 'fila':
      return OrderStatus.PENDING;
    case 'em_preparo':
      return OrderStatus.IN_PREPARATION;
    case 'pronto':
      return OrderStatus.READY;
    case 'entregue':
      return OrderStatus.DELIVERED;
    case 'cancelado':
      return OrderStatus.CANCELLED;
    default:
      return OrderStatus.PENDING;
  }
}

// Export validation rules
export { ORDER_VALIDATION_RULES };
